import { Toaster } from "@/components/ui/sonner";
import { DateRangeType, formatDate } from "@/helper/date-time";
import { PermitLocationDetail } from "@/modules/restriction/components/PermitLocationDetail";
import { DecreeListingPanel, RestrictionDetail } from "./components";
import { Feature, Point, Polygon } from "geojson";
import { MapStyle, SearchPlace, SelectMapType, SwitchModeView } from "../curb-zone/components";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { Restriction, RestrictionPageParams } from "types";
import {
  RestrictionModeType,
  multipleRestrictionMode,
  toolboxIcons,
  trafficRestrictionMode,
} from "./components/map-tool-box/restriction-tool-box-entry.const";

import { DateRangeSelector } from "components/form-control/DateRangeSelector";
import { RestrictionModal } from "./components/restriction-modal";
import { LocationModalState } from "@/types/address.type";
import { MapToolBox } from "@/components/map-tool-box";
import { ModeViewType } from "constants/map.const";
import { PermitLocation } from "@/types/decree.type";
import { PermitLocationModal } from "@/modules/restriction/components/PermitLocationModal";
import { PickingInfo } from "deck.gl";
import { Range } from "react-date-range";
import { RestrictionByDecree } from "./components/restriction-by-decree";
import { RestrictionMultiplePrompt } from "./components/restriction-multiple-prompt";
import { RestrictionTooltip } from "./components/restriction-tooltip";
import { SelectedMultipleCurbZoneDetail } from "./components/selected-multiple-curb-zone-detail";
import { SelectedMultipleCurbZoneRestrictionDetail } from "./components/selected-multiple-curb-zone-restriction-detail";
import { Switch } from "@/components/ui/switch";
import { ViewState } from "react-map-gl/mapbox";
import { getAddressByPoint } from "@/services/web/mapbox.service";
import { getQueryParams } from "helper/utils";
import { isEmpty } from "lodash";
import { restrictionCursorCell } from "@/modules/restriction/restriction.const";
import { usePermitLocationFeature } from "@/modules/restriction/hooks/use-permit-location-feature";
import { useRestriction } from "./hooks/use-restriction";
import { useSetQueryParams } from "@/hooks/use-set-query-params";
import { useState } from "react";

export const RestrictionMap = () => {
  const [appMode, setAppMode] = useState<RestrictionModeType>(RestrictionModeType.View);
  const params = getQueryParams<RestrictionPageParams>();
  const [selectedDateRangeType, setSelectedDateRangeType] = useState<DateRangeType>();

  const [selectedDate, setSelectedDate] = useState<Range>(() => ({
    startDate: params.disabledStartDate ? new Date(params.disabledStartDate) : undefined,
    endDate: params.disabledEndDate ? new Date(params.disabledEndDate) : undefined,
  }));

  const setQueryParams = useSetQueryParams();

  const handleChangeDate = (item: Range, type?: DateRangeType) => {
    setSelectedDate(item);
    if (type) {
      setSelectedDateRangeType(type);
    }
    setQueryParams(
      {
        disabledStartDate: item.startDate
          ? formatDate(item.startDate.toDateString(), { format: "MM-dd-yyyy" })
          : undefined,
        disabledEndDate: item.endDate ? formatDate(item.endDate.toDateString(), { format: "MM-dd-yyyy" }) : undefined,
      },
      { merge: true },
    );
  };

  const {
    markerPosition,
    selectAddress,
    viewport,
    mapType,
    setMapType,
    modeView,
    handleDragEnd,
    handleSelectCurbZoneRestriction,
    handleZooming,
    onViewStateChange,
    setModeView,
    currentCity,
    showRestrictionDetail,
    handleRemoveRestriction,
    handleRemoveMultipleCurbZoneRestrictions,
    setOpenRestrictionModal,
    setOpenCurbZoneRestrictionMultipleModal,
    openRestrictionModal,
    cancelEditRestriction,
    handleSaveRestriction,
    restrictionData,
    preZooming,
    selectedDecree,
    restrictionByDecree,
    handleSelectDecree,
    wrapMapRef,
    restrictionToolTipData,
    isSpaceBarPressed,
    showCurbZoneMultiDetail,
    showCurbZoneRestrictionMultiDetail,
    restrictionDetail,
    cancelCreateMultiple,
    cancelEditMultiple,
    changeMultipleCurbZoneRestriction,
    clickDescriptionRestriction,
    cancelEditTrafficRestriction,
    handleSelectTrafficRestriction,
    openCurbZoneRestrictionMultipleModal,
    mapLayers,
    isShowCurbZoneRestriction,
    setIsShowCurbZoneRestriction,
    resetHoverStates,
  } = useRestriction({ appMode, selectedDate });

  const {
    permitLocationMapLayers,
    savePermitLocation,
    handleRemovePermitLocation,
    permitLocationModalState,
    permitLocationDetail,
    setPermitLocationDetail,
    setPermitLocationModalState,
    handleClose,
  } = usePermitLocationFeature({ appMode });

  const cancelEdit = (isEdit: boolean) => {
    if (trafficRestrictionMode.includes(appMode)) {
      cancelEditTrafficRestriction(isEdit);
    } else {
      cancelEditRestriction(isEdit);
    }
  };

  const onClickOnMap = async (info: PickingInfo) => {
    if (
      appMode === RestrictionModeType.AddPermitLocation &&
      permitLocationModalState === LocationModalState.DEACTIVATE
    ) {
      //Add a new permit location
      const newPoint = { type: "Point", coordinates: info.coordinate } as Point;
      const address = await getAddressByPoint(newPoint);
      setPermitLocationDetail({
        id: "",
        address,
        position: newPoint,
        selectedDecrees: [],
      });
      setPermitLocationModalState(LocationModalState.SHOW);
    } else if (permitLocationModalState === LocationModalState.MINIMIZE && info.coordinate) {
      //for the permit location, select a point in the map to get the location
      setPermitLocationDetail(
        (prevState) =>
          ({
            ...prevState,
            position: { type: "Point", coordinates: info.coordinate },
          }) as PermitLocation,
      );
    } else if (trafficRestrictionMode.includes(appMode)) {
      handleSelectTrafficRestriction(info);
    } else {
      handleSelectCurbZoneRestriction(info);
    }
  };

  const handleSelectDescriptionCard = (input: Feature<Polygon, Restriction>) => {
    clickDescriptionRestriction(input);
  };

  const getCursor = () => {
    if (restrictionCursorCell.includes(appMode)) {
      return () => "cell";
    }
    if (multipleRestrictionMode.includes(appMode)) {
      if (isSpaceBarPressed) {
        return ({ isDragging }: { isDragging: boolean }) => (isDragging ? "grabbing" : "grab");
      }
      return () => "cell";
    }
    return ({ isDragging }: { isDragging: boolean }) => (isDragging ? "grabbing" : "grab");
  };

  return (
    <div className="h-full top-0 w-full absolute">
      <ResizablePanelGroup direction="vertical" className="border">
        <ResizablePanel>
          <div className="relative h-full" ref={wrapMapRef} onMouseLeave={resetHoverStates}>
            <div className="absolute top-16 left-6 z-10 flex gap-2">
              <SearchPlace onSelect={selectAddress} coordinates={viewport} />
              <SelectMapType mapType={mapType} onSelectMapType={(value) => setMapType(value)} />
              <DateRangeSelector
                variant="secondary"
                onChangeValue={handleChangeDate}
                from={selectedDate.startDate}
                to={selectedDate.endDate}
                selectedValue={selectedDateRangeType}
              />
            </div>
            <div>
              <MapStyle
                markerPosition={markerPosition}
                mapType={mapType}
                getCursor={getCursor}
                modeView={modeView}
                layers={[...mapLayers, ...permitLocationMapLayers]}
                handleDragEnd={handleDragEnd}
                handleSelectLayer={onClickOnMap}
                handleZooming={(event) => handleZooming(event, preZooming.current)}
                onViewStateChange={({ viewState }) => onViewStateChange(viewState as unknown as ViewState)}
                viewport={viewport}
                isSpaceBarPressed={isSpaceBarPressed}
              />
            </div>
            {mapType === "Map box" && (
              <SwitchModeView modeView={modeView} selectModeView={(mode: ModeViewType) => setModeView(mode)} />
            )}
            {multipleRestrictionMode.includes(appMode) && (
              <div className="absolute p-4 bottom-2 left-24 ml-6 bg-default rounded-md shadow-lg">
                <p>Press SPACE bar to move the map.</p>
                <p>
                  Press SHIFT button to select one curb-zone
                  {appMode === RestrictionModeType.MultiEditRestriction ? " restriction" : ""}.
                </p>
                <p>
                  Press ALT button to select multiple curb-zones
                  {appMode === RestrictionModeType.MultiEditRestriction ? " restriction" : ""}.
                </p>
              </div>
            )}
            <div className="absolute top-36 left-8 z-6">
              <MapToolBox appMode={appMode} setAppMode={setAppMode} toolboxIcons={toolboxIcons} />
            </div>
            <div className="absolute top-36 left-36 pt-1">
              <Switch
                id="restriction-mode"
                checked={isShowCurbZoneRestriction}
                onCheckedChange={setIsShowCurbZoneRestriction}
              />
            </div>
            {(appMode === RestrictionModeType.View ||
              appMode === RestrictionModeType.EditCurbZoneRestriction ||
              appMode === RestrictionModeType.EditTrafficRestriction) &&
              !isEmpty(restrictionByDecree) && (
                <RestrictionByDecree
                  onSelectDescriptionCard={handleSelectDescriptionCard}
                  listRestrictions={restrictionByDecree || []}
                  selectedDescriptionCardId={restrictionDetail[0]?.id}
                />
              )}
          </div>
          {restrictionData
            ? showRestrictionDetail && (
                <RestrictionDetail
                  id={restrictionData?.id as string}
                  restriction={restrictionData}
                  deleteRestriction={handleRemoveRestriction}
                  openRestrictionModal={setOpenRestrictionModal}
                />
              )
            : null}
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel className="!overflow-y-auto">
          <DecreeListingPanel selectedDecree={selectedDecree?.id} onSelectDecree={handleSelectDecree} />
        </ResizablePanel>
        {restrictionToolTipData && (
          <RestrictionTooltip
            left={restrictionToolTipData.x}
            top={restrictionToolTipData.y}
            curbZoneHover={restrictionToolTipData.curbZoneHover}
            restrictionHover={restrictionToolTipData.restrictionHover}
          />
        )}
      </ResizablePanelGroup>
      {openRestrictionModal && restrictionData && (
        <RestrictionModal saveRestriction={handleSaveRestriction} prefill={restrictionData} cancel={cancelEdit} />
      )}
      {currentCity
        ? showCurbZoneMultiDetail && (
            <SelectedMultipleCurbZoneDetail
              curbZones={restrictionDetail}
              resetState={cancelCreateMultiple}
              openCurbZoneMultiModal={setOpenCurbZoneRestrictionMultipleModal}
            />
          )
        : null}
      {currentCity
        ? showCurbZoneRestrictionMultiDetail && (
            <SelectedMultipleCurbZoneRestrictionDetail
              curbZoneRestrictions={restrictionDetail}
              resetState={cancelCreateMultiple}
              openCurbZoneMultiModal={setOpenCurbZoneRestrictionMultipleModal}
              handleRemoveMultipleCurbZoneRestrictions={handleRemoveMultipleCurbZoneRestrictions}
            />
          )
        : null}
      {openCurbZoneRestrictionMultipleModal && restrictionData && (
        <RestrictionMultiplePrompt
          saveRestriction={changeMultipleCurbZoneRestriction}
          prefill={restrictionData}
          cancel={cancelEditMultiple}
        />
      )}
      {permitLocationModalState !== LocationModalState.DEACTIVATE && permitLocationDetail && (
        <PermitLocationModal
          coordinates={viewport}
          permitLocation={permitLocationDetail}
          onClose={handleClose}
          onSave={savePermitLocation}
          permitLocationModalState={permitLocationModalState}
          setPermitLocationModalState={setPermitLocationModalState}
          setPermitLocationDetail={setPermitLocationDetail}
        />
      )}
      {permitLocationModalState !== LocationModalState.MINIMIZE && permitLocationDetail && permitLocationDetail?.id && (
        <PermitLocationDetail
          setPermitLocationModalState={() => setPermitLocationModalState(LocationModalState.SHOW)}
          permitLocation={permitLocationDetail}
          removePermitLocation={handleRemovePermitLocation}
          onClose={handleClose}
        />
      )}
      <Toaster position="top-right" />
    </div>
  );
};
